import React from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Target, ChartLine, Rocket, CheckCircle, Star, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SeoFeatures = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: <MagnifyingGlass className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.keywordResearch.title'),
      description: t('seoFeatures.keywordResearch.description')
    },
    {
      icon: <Target className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.technicalSeo.title'),
      description: t('seoFeatures.technicalSeo.description')
    },
    {
      icon: <ChartLine className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.contentOptimization.title'),
      description: t('seoFeatures.contentOptimization.description')
    },
    {
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.localSeo.title'),
      description: t('seoFeatures.localSeo.description')
    },
    {
      icon: <CheckCircle className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.linkBuilding.title'),
      description: t('seoFeatures.linkBuilding.description')
    },
    {
      icon: <Star className="w-8 h-8" weight="thin" />,
      title: t('seoFeatures.analytics.title'),
      description: t('seoFeatures.analytics.description')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Wat onze <span className="text-blue-600">SEO service</span> omvat
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Van keyword onderzoek tot technische optimalisatie. We bieden complete SEO-oplossingen 
            die je vindbaarheid verbeteren en meer organisch verkeer aantrekken.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {features.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative"
            >
              <div className="bg-white rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
                      <div className="bg-blue-900 rounded-3xl p-12 text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6">
                              <Star className="w-8 h-8 text-blue-900" weight="fill" />
            </div>
            <h3 className="text-3xl font-bold mb-4">Klaar om je SEO te verbeteren?</h3>
                          <p className="text-blue-200 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
              We maken je website vindbaar voor de juiste zoekwoorden en zorgen voor continue groei 
              in organisch verkeer. Van audit tot implementatie - we begeleiden je bij elke stap.
            </p>
            <Link to="/start-your-project" className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <ChartLine weight="thin" className="w-6 h-6" />
                Start je SEO project
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" weight="thin" />
              </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeoFeatures;
